import { DefaultAnswerEnum } from '@ulysses-inc/harami_api_client'
import { isNullish } from '~/utils/isNullish'
import type { NumberRule } from './numberRule'
import type { DeviateProperty, QuestionBase } from '../question'

export type NumberQuestion = QuestionBase<'number'> &
  DeviateProperty & {
    rule: NumberRule | undefined
    // 小数点以下の桁数を表す。value: number | undefined などでも表現できるが、
    // 0 と undefined が混在することを避けるため、オブジェクトで wrap するような形で定義している
    decimalPoint:
      | {
          value: number
        }
      | undefined
    scale: string
    defaultAnswer?: {
      type: DefaultAnswerEnum
      // 製品マスターに該当する値がない場合は null になる
      value: number | null
    }
    // 製品マスターからルールを取得するかどうか
    // true の場合、製品情報がないと rule が undefined になる
    isRuleFromProductMaster: boolean
  }

const isWithinNumberValueRule = (rule: NumberRule, value: number): boolean => {
  switch (rule.type) {
    case 'between':
      return value >= rule.minimum && value <= rule.maximum
    case 'le':
      return value <= rule.maximum
    case 'ge':
      return value >= rule.minimum
  }
}

const convertNumberRuleCharacters = (
  scale: string,
  rule?: NumberRule,
): string => {
  switch (rule?.type) {
    case 'between':
      return `${rule.minimum}${scale}〜${rule.maximum}${scale}の間`
    case 'le':
      return `${rule.maximum}${scale}以下`
    case 'ge':
      return `${rule.minimum}${scale}以上`
    default:
      return 'ルールなし'
  }
}

const isProductMasterAnswer = (question: NumberQuestion): boolean => {
  if (question.defaultAnswer?.type === DefaultAnswerEnum.ProductMaster) {
    return true
  }
  return false
}

/**
 * 製品マスターに該当するデータがない場合を判定
 * - defaultAnswer.value が null または undefined の場合
 * - 製品マスターからルールを取得する設定で、rule が undefined の場合
 */
const hasNoProductMasterData = (question: NumberQuestion): boolean => {
  // 製品マスターの値がない（null または undefined）
  // NOTE: API型定義では value: number | null だが、実際のAPIレスポンスでは
  // valueフィールド自体が存在しない場合がある（undefined）ため、isNullishを使用
  if (
    question.defaultAnswer?.type === DefaultAnswerEnum.ProductMaster &&
    isNullish(question.defaultAnswer.value)
  ) {
    return true
  }
  // 製品マスターからルールを取得する設定で、ルールがない
  if (question.isRuleFromProductMaster && question.rule === undefined) {
    return true
  }
  return false
}

export const NumberQuestion = {
  isWithinNumberValueRule,
  convertNumberRuleCharacters,
  isProductMasterAnswer,
  hasNoProductMasterData,
}
